import React, { useState, useContext, useEffect, useRef } from "react";
import { Eye, EyeOff } from "lucide-react";
import senzoLogo from '../assets/SENZO_SINCE 1995_32KB.png';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/axiosConfig';
import { GatewayDataContext } from "../dataContext/GatewayDataContext";
import { useAuth } from '../dataContext/AuthContext';
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";

function Login() {
  const { setIsAuthenticated } = useContext(GatewayDataContext);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const submittingRef = useRef(false); // immediate guard against double submit (Enter + click)
    const navigate = useNavigate();
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const { updateToken, updateOrganizationId, updateUserId, updateEmailAddress, updateFirstName, updateLastName, updateContactNumber } = useAuth();
    function handleInputChange(event) {
        const { name, value } = event.target;
        if (name === 'username') {
            setUsername(value);
        } else if (name === 'password') {
            setPassword(value);
        }
    }

    useEffect(() => {
      const handleAuthError = () => {
          setIsAuthenticated(false);
          navigate('/login');
      };

      document.addEventListener('authError', handleAuthError);
      
      return () => {
          document.removeEventListener('authError', handleAuthError);
      };
  }, [navigate, setIsAuthenticated]);

  useEffect(() => {
    const suspensionMessage = localStorage.getItem('suspensionMessage');
    if (suspensionMessage) {
      toast.error(suspensionMessage, {
        autoClose: 5000, // Keep it visible longer
        position: "top-center", // Make it more visible
      });
      localStorage.removeItem('suspensionMessage');
    }
  }, []);
    async function handleLoginSubmit(event) {
      event.preventDefault(); // Keep this
      event.stopPropagation();
      if (submittingRef.current) return;
      setErrorMessage(""); // Clear any previous error messages
      
      if (!username || !password) {
        setErrorMessage("Please enter both username and password");
        return; // Early return if validation fails
      }
      submittingRef.current = true;
      setSubmitting(true);
    
      try {
        const response = await apiClient.post(
          `${apiBaseUrl}/auth/login`,
          {
            email: username,
            password,
          },
          {
            withCredentials: true,
          }
        );
    
        const { token, organizationId, userId, emailAddress, firstName, lastName, contactNumber } = response.data;

        if (!token) {
          setErrorMessage("No token returned");
          submittingRef.current = false;
          setSubmitting(false);
          return;
        }

        // Decode token to get roles BEFORE navigation
        let userRoles = [];
        try {
          const decodedToken = jwtDecode(token);
          const rolesFromToken = decodedToken["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
          userRoles = Array.isArray(rolesFromToken) ? rolesFromToken : rolesFromToken ? [rolesFromToken] : [];
        } catch (error) {
          console.error("Error decoding token:", error);
        }

        // Update all auth context values
        updateToken(token);
        if (organizationId) updateOrganizationId(organizationId);
        if (userId) updateUserId(userId);
        if (emailAddress) updateEmailAddress(emailAddress);
        if (firstName) updateFirstName(firstName);
        if (lastName) updateLastName(lastName);
        if (contactNumber) updateContactNumber(contactNumber);
        setIsAuthenticated(true);

        // Wait a tick to ensure context updates propagate
        await new Promise(resolve => setTimeout(resolve, 150));

        // Determine route based on roles decoded from token
        if(userRoles.includes('customer_user')) {
          navigate("/customer_dashboard");
        }
        else {
          navigate("/dashboard");
        }
      } catch (error) { 
        const errorMsg = error.response?.data?.errorMessage ||  
                        error.response?.data?.message || 
                        "Login failed. Please try again.";
        setErrorMessage(errorMsg);
        console.error("Login failed:", error);
        submittingRef.current = false;
        setSubmitting(false);
      }
    }
    // Get frontend version from package.json
    const frontendVersion = process.env.REACT_APP_VERSION || "1.0.1";

    return (
      <div className="min-h-screen flex items-center justify-center bg-blue-600">
        <div className="bg-white w-full max-w-md mx-4 md:mx-auto p-6 md:p-8 space-y-6 rounded-lg shadow-lg">
          <div className="flex justify-center">
          <img src={senzoLogo} alt="senzoLogo" className="h-auto w-auto max-h-32" />
          </div>
          <form noValidate className="space-y-6" onSubmit={handleLoginSubmit}>
          <div>
            <label htmlFor="username" className="sr-only">
              Username
            </label>
            <div className="relative">
              <input
                onChange={handleInputChange}
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                disabled={submitting}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Username"
              />
              
            </div>
          </div>
          <div>
            <label htmlFor="password" className="sr-only">
              Password
            </label>
            <div className="relative">
              <input
              onChange={handleInputChange}
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                disabled={submitting}
                required
                className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          <div>
              {errorMessage && (
                <div role="alert" className="text-red-600 text-sm mt-2">
                  {errorMessage}
                </div>
              )}
            </div>
            <div>
              <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
            {submitting && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" aria-hidden="true" />
            )}
            <span className="">{submitting ? "LOGGING IN..." : "LOGIN"}</span>
            </button>
          </div>
        </form>
        {/* Version Info Footer */}
        <div className="text-center text-xs text-gray-500 mt-4">
          <p>Version {frontendVersion}</p>
        </div>
        </div>
      </div>
    );
}

export default Login;